import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

marked.setOptions({ gfm: true, breaks: true })

export default class extends Controller {
  static targets = [
    "panel", "toggle", "messages", "input", "imagePreview", "imagePreviewImg",
    "sendButton", "contextIndicator", "locationLabel", "undoBar", "typingIndicator", "typingText",
    "mainView", "historyPanel", "historyList", "alternativeSlots"
  ]

  static values = {
    open: { type: Boolean, default: false },
    context: { type: String, default: "" },
    locationId: { type: String, default: "" }
  }

  connect() {
    this.conversationHistory = this.conversationHistory || []
    this.currentConversationId = null
    this.imageData = null
    this.formSnapshot = null
    this.lastAppliedFields = null
    this.undoTimeout = null

    // Initialize conversation from DB (or create new)
    this._initConversation()

    // Update context and location label on every Turbo navigation
    // (panel is turbo-permanent so connect() only runs once)
    this._onTurboLoad = () => {
      this.detectContext()
      this._updateLocationLabel()
      this._checkPrefill()
    }
    document.addEventListener("turbo:load", this._onTurboLoad)

    this._checkPrefill()
  }

  disconnect() {
    document.removeEventListener("turbo:load", this._onTurboLoad)
  }

  // --- Conversation DB persistence ---

  _convStorageKey() {
    return `zenna_conv_id_${this.locationIdValue || "default"}`
  }

  async _initConversation() {
    const savedId = sessionStorage.getItem(this._convStorageKey())

    if (savedId) {
      try {
        const conv = await this._fetchConversation(savedId)
        this.currentConversationId = conv.id
        this.conversationHistory = conv.messages || []
        if (this.conversationHistory.length > 0) {
          this._renderConversationMessages(this.conversationHistory)
        }
        return
      } catch (e) {
        sessionStorage.removeItem(this._convStorageKey())
      }
    }

    await this._createConversation()
  }

  async _createConversation() {
    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch("/dashboard/zenna_conversations", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({})
      })
      if (response.ok) {
        const data = await response.json()
        this.currentConversationId = data.id
        sessionStorage.setItem(this._convStorageKey(), String(data.id))
      }
    } catch (e) {
      // Non-fatal — chat still works without DB persistence
    }
  }

  async _fetchConversation(id) {
    const response = await fetch(`/dashboard/zenna_conversations/${id}`, {
      headers: { "Accept": "application/json" }
    })
    if (!response.ok) throw new Error("Not found")
    return response.json()
  }

  async _syncConversation(guestName) {
    if (!this.currentConversationId) return
    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const body = { messages: this.conversationHistory }
      if (guestName) body.guest_name = guestName
      await fetch(`/dashboard/zenna_conversations/${this.currentConversationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify(body)
      })
    } catch (e) {
      // Non-fatal
    }
  }

  _renderConversationMessages(messages) {
    this._clearMessages()
    messages.forEach(msg => {
      if (msg.role === "user") {
        this.appendUserMessage(msg.content)
      } else if (msg.role === "assistant") {
        this.appendZennaMessage(msg.content)
      }
    })
  }

  // --- History panel ---

  async openHistory() {
    try {
      const response = await fetch("/dashboard/zenna_conversations", {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return
      const conversations = await response.json()
      this._renderHistoryList(conversations)
    } catch (e) {
      return
    }

    this.mainViewTarget.classList.add("hidden")
    this.historyPanelTarget.classList.remove("hidden")
  }

  closeHistory() {
    this.historyPanelTarget.classList.add("hidden")
    this.mainViewTarget.classList.remove("hidden")
  }

  _renderHistoryList(conversations) {
    const list = this.historyListTarget
    list.innerHTML = ""

    if (conversations.length === 0) {
      list.innerHTML = `<p class="text-center text-olive text-sm py-8">No conversations yet</p>`
      return
    }

    conversations.forEach(conv => {
      const item = document.createElement("button")
      item.className = "w-full flex items-center gap-3 px-4 py-3 hover:bg-cream/60 text-left border-b border-sand/20 transition-colors"
      item.dataset.action = "ai-chat#loadConversation"
      item.dataset.convId = conv.id

      const initials = this._initials(conv.guest_name)
      const color = this._avatarColor(conv.guest_name)
      const time = this._relativeTime(conv.last_message_at)
      const isActive = conv.id === this.currentConversationId

      item.innerHTML = `
        <div class="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${color}">${this.escapeHtml(initials)}</div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <span class="font-medium text-sm text-charcoal truncate">${this.escapeHtml(conv.guest_name)}</span>
            <span class="text-xs text-olive flex-shrink-0">${this.escapeHtml(time)}</span>
          </div>
          <p class="text-xs text-olive truncate mt-0.5">${this.escapeHtml(conv.last_message_preview || "")}</p>
        </div>
        ${isActive ? '<span class="w-2 h-2 rounded-full bg-zenna flex-shrink-0"></span>' : ""}
      `
      list.appendChild(item)
    })
  }

  async loadConversation(event) {
    const id = parseInt(event.currentTarget.dataset.convId)
    try {
      const conv = await this._fetchConversation(id)
      this.currentConversationId = conv.id
      this.conversationHistory = conv.messages || []
      sessionStorage.setItem(this._convStorageKey(), String(conv.id))
      this._renderConversationMessages(this.conversationHistory)
      this.closeHistory()
    } catch (e) {
      // ignore
    }
  }

  async newConversation() {
    this.conversationHistory = []
    this.currentConversationId = null
    this._clearMessages()
    await this._createConversation()
    this.closeHistory()
  }

  // --- Avatar helpers ---

  _initials(name) {
    if (!name || name === "New conversation") return "?"
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase()
  }

  _avatarColor(name) {
    const colors = [
      "bg-zenna", "bg-primary", "bg-accent", "bg-olive",
      "bg-teal-500", "bg-rose-400", "bg-amber-500", "bg-indigo-400"
    ]
    if (!name) return colors[0]
    let hash = 0
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) & 0xffffffff
    return colors[Math.abs(hash) % colors.length]
  }

  _relativeTime(iso) {
    if (!iso) return ""
    const diff = Date.now() - new Date(iso).getTime()
    const min = Math.floor(diff / 60000)
    if (min < 1) return "just now"
    if (min < 60) return `${min}m ago`
    const hrs = Math.floor(min / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days === 1) return "Yesterday"
    if (days < 7) return new Date(iso).toLocaleDateString("en", { weekday: "short" })
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
  }

  // --- Location label ---

  _updateLocationLabel() {
    if (!this.hasLocationLabelTarget) return
    const sidebarLocation = document.querySelector("[data-testid='location-switch']")
    if (sidebarLocation) {
      const selected = sidebarLocation.options[sidebarLocation.selectedIndex]
      if (selected) {
        this.locationLabelTarget.textContent = selected.text.split(",")[0].trim()

        const newLocationId = String(selected.value)
        if (newLocationId && newLocationId !== this.locationIdValue) {
          this.locationIdValue = newLocationId
          this.conversationHistory = []
          this.currentConversationId = null
          this._clearMessages()
          this._initConversation()
        }
      }
    }
  }

  _clearMessages() {
    const messages = this.messagesTarget
    const welcome = messages.querySelector("div:first-child")
    messages.innerHTML = ""
    if (welcome) messages.appendChild(welcome)
  }

  _checkPrefill() {
    const pending = sessionStorage.getItem("zenna_prefill")
    if (pending) {
      sessionStorage.removeItem("zenna_prefill")
      requestAnimationFrame(() => {
        const form = document.querySelector("form.ai-fillable")
        if (form) {
          const fields = JSON.parse(pending)
          this.openValue = true
          this.panelTarget.classList.remove("hidden")
          this.detectContext()
          this.applyToForm(fields)
        }
      })
    }
  }

  togglePanel() {
    this.openValue = !this.openValue

    if (this.openValue) {
      this.panelTarget.classList.remove("hidden")
      this.detectContext()
      this.inputTarget.focus()
    } else {
      this.panelTarget.classList.add("hidden")
    }
  }

  detectContext() {
    const path = window.location.pathname
    let context = "booking"

    if (path.match(/\/dashboard\/treatments\/(new|\d+\/edit)/)) {
      context = "treatment"
    } else if (path.match(/\/dashboard\/therapists\/(new|\d+\/edit)/)) {
      context = "therapist"
    }

    this.contextValue = context

    if (context !== "general" && this.hasContextIndicatorTarget) {
      this.contextIndicatorTarget.textContent = context
      this.contextIndicatorTarget.classList.remove("hidden")
    } else if (this.hasContextIndicatorTarget) {
      this.contextIndicatorTarget.classList.add("hidden")
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.send()
    }
  }

  handlePaste(event) {
    const items = event.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault()
        const file = item.getAsFile()
        if (file.size > 5 * 1024 * 1024) {
          this.appendZennaMessage("That image is too large (max 5MB). Please try a smaller one.")
          return
        }
        this.readImageFile(file)
        return
      }
    }
  }

  readImageFile(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      this.imageData = e.target.result
      this.imagePreviewImgTarget.src = this.imageData
      this.imagePreviewTarget.classList.remove("hidden")
    }
    reader.readAsDataURL(file)
  }

  removeImage() {
    this.imageData = null
    this.imagePreviewTarget.classList.add("hidden")
    this.imagePreviewImgTarget.src = ""
  }

  async send() {
    const message = this.inputTarget.value.trim()
    if (!message && !this.imageData) return

    if (message) this.appendUserMessage(message)
    if (this.imageData) this.appendUserImage()

    this.conversationHistory.push({ role: "user", content: message || "(image)" })

    this.inputTarget.value = ""
    const imageToSend = this.imageData
    this.removeImage()

    this.setLoading(true)

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch("/dashboard/ai_chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/event-stream"
        },
        body: JSON.stringify({
          message: message,
          image: imageToSend,
          context_type: this.contextValue || "general"
        })
      })

      if (response.status === 429) {
        this.appendZennaMessage("I need a short break — too many messages. Please try again in a few minutes.")
        return
      }

      if (!response.ok) {
        this.appendZennaMessage("Something went wrong. Please try again.")
        return
      }

      await this._consumeStream(response)
    } catch (err) {
      this.appendZennaMessage("Sorry, I couldn't connect. Please check your network and try again.")
    } finally {
      this.setLoading(false)
    }
  }

  async _consumeStream(response) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    let replyText = ""
    let guestName = null
    let synced = false

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() // keep incomplete last line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const raw = line.slice(6).trim()
          if (!raw) continue

          let chunk
          try { chunk = JSON.parse(raw) } catch { continue }

          if (chunk.type === "status") {
            this._setTypingText(chunk.message)

          } else if (chunk.type === "complete") {
            replyText = chunk.content || ""
            this.appendZennaMessage(replyText)
            this.conversationHistory.push({ role: "assistant", content: replyText })
            this.toggleTarget.classList.add("zenna-toggle--response")
            setTimeout(() => this.toggleTarget.classList.remove("zenna-toggle--response"), 300)

          } else if (chunk.type === "fields") {
            const fields = chunk.fields || {}
            if (this.hasRequiredFields(fields)) {
              guestName = fields.guest_name || null
              this.showVerificationCard(fields, "high")
            }

          } else if (chunk.type === "error") {
            this.appendZennaMessage(chunk.message || "Something went wrong. Please try again.")
          }
        }
      }
    } finally {
      reader.releaseLock()
      if (replyText && !synced) {
        synced = true
        this._syncConversation(guestName)
      }
    }
  }

  _setTypingText(text) {
    if (this.hasTypingTextTarget) {
      this.typingTextTarget.textContent = text
    }
  }

  setLoading(loading) {
    this.sendButtonTarget.disabled = loading
    this.inputTarget.disabled = loading

    if (loading) {
      this.typingIndicatorTarget.classList.remove("hidden")
      this.toggleTarget.classList.add("zenna-toggle--thinking")
    } else {
      this.typingIndicatorTarget.classList.add("hidden")
      this.toggleTarget.classList.remove("zenna-toggle--thinking")
      this._setTypingText("Zenna is reading...")
      this.inputTarget.focus()
    }

    this.scrollToBottom()
  }

  // --- Message rendering ---

  appendUserMessage(text) {
    const div = document.createElement("div")
    div.className = "flex justify-end"
    div.innerHTML = `<div class="bg-gold/25 rounded-lg rounded-tr-none px-3 py-2 text-sm text-charcoal max-w-[85%]">${this.escapeHtml(text)}</div>`
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  appendUserImage() {
    const div = document.createElement("div")
    div.className = "flex justify-end"
    div.innerHTML = `<div class="bg-primary/10 rounded-lg rounded-tr-none px-2 py-2 max-w-[85%]"><img src="${this.imageData}" class="h-24 rounded" /></div>`
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  appendZennaMessage(text) {
    const html = marked.parse(text)
    const div = document.createElement("div")
    div.className = "flex items-start gap-2"
    div.innerHTML = `
      <div class="w-6 h-6 bg-zenna rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"><path class="zenna-sparkle-1" d="M9.5 2a1 1 0 0 1 .95.684l.39 1.132a3.2 3.2 0 0 0 1.994 1.994l1.132.39a1 1 0 0 1 0 1.893l-1.132.39a3.2 3.2 0 0 0-1.994 1.994l-.39 1.132a1 1 0 0 1-1.893 0l-.39-1.132A3.2 3.2 0 0 0 5.224 7.09l-1.132-.39a1 1 0 0 1 0-1.893l1.132-.39A3.2 3.2 0 0 0 7.218 2.42l.39-1.132A1 1 0 0 1 9.558 2Z" fill="white"/><path class="zenna-sparkle-2" d="M18 12a1 1 0 0 1 .946.677l.253.734a2.4 2.4 0 0 0 1.496 1.496l.734.253a1 1 0 0 1 0 1.893l-.734.253a2.4 2.4 0 0 0-1.496 1.496l-.253.734a1 1 0 0 1-1.893 0l-.253-.734a2.4 2.4 0 0 0-1.496-1.496l-.734-.253a1 1 0 0 1 0-1.893l.734-.253a2.4 2.4 0 0 0 1.496-1.496l.253-.734A1 1 0 0 1 18 12Z" fill="white"/><path class="zenna-sparkle-3" d="M6 14a1 1 0 0 1 .942.662l.108.312a1.6 1.6 0 0 0 .996.996l.312.108a1 1 0 0 1 0 1.884l-.312.108a1.6 1.6 0 0 0-.996.996l-.108.312a1 1 0 0 1-1.884 0l-.108-.312a1.6 1.6 0 0 0-.996-.996l-.312-.108a1 1 0 0 1 0-1.884l.312-.108a1.6 1.6 0 0 0 .996-.996l.108-.312A1 1 0 0 1 6 14Z" fill="white"/></svg>
      </div>
      <div class="bg-zenna/20 rounded-lg rounded-tl-none px-3 py-2 text-sm text-charcoal max-w-[85%] zenna-markdown">${html}</div>
    `
    this.messagesTarget.appendChild(div)
    this.scrollToBottom()
  }

  // --- Verification card ---

  showVerificationCard(fields, confidence) {
    const card = document.createElement("div")
    card.className = "mx-2 my-2 bg-white border border-accent rounded-lg p-4 text-sm"

    let fieldsHtml = '<div class="space-y-2 mb-3">'
    for (const [key, value] of Object.entries(fields)) {
      const label = this.humanizeFieldName(key)
      const inputType = this.getFieldInputType(key)

      if (inputType === "select") {
        fieldsHtml += this.buildSelectField(key, label, value)
      } else if (inputType === "checkbox") {
        fieldsHtml += `
          <label class="flex items-center gap-2">
            <input type="checkbox" data-field-key="${key}" ${value ? "checked" : ""} class="rounded border-sand/40 text-zenna focus:ring-zenna/30" />
            <span class="text-olive text-xs">${this.escapeHtml(label)}</span>
          </label>
        `
      } else {
        fieldsHtml += `
          <div>
            <label class="text-olive text-xs">${this.escapeHtml(label)}</label>
            <input type="text" data-field-key="${key}" value="${this.escapeHtml(String(value))}" class="w-full border border-sand/40 rounded px-2 py-1 text-sm text-charcoal focus:border-zenna focus:ring-1 focus:ring-zenna/30" />
          </div>
        `
      }
    }
    fieldsHtml += '</div>'

    card.innerHTML = `
      <div class="flex items-center gap-1 text-zenna-dark font-medium mb-3">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
        Ready to apply
      </div>
      ${fieldsHtml}
      <div class="flex items-center gap-3">
        <button data-action="ai-chat#applyFromCard" class="bg-zenna text-white text-sm px-4 py-1.5 rounded hover:bg-zenna-dark transition-colors">Apply to Form</button>
        <button data-action="ai-chat#cancelCard" class="text-olive underline text-sm hover:text-charcoal">Cancel</button>
      </div>
    `

    card.dataset.aiChatTarget = "verificationCard"
    this.messagesTarget.appendChild(card)
    this.scrollToBottom()
  }

  applyFromCard(event) {
    const card = event.target.closest("[data-ai-chat-target='verificationCard']")
    if (!card) return

    const fields = {}
    card.querySelectorAll("[data-field-key]").forEach(input => {
      const key = input.dataset.fieldKey
      if (input.type === "checkbox") {
        fields[key] = input.checked
      } else {
        fields[key] = input.value
      }
    })

    this.applyToForm(fields)

    card.querySelectorAll("button").forEach(btn => btn.disabled = true)
    card.classList.add("opacity-60")
  }

  cancelCard(event) {
    const card = event.target.closest("[data-ai-chat-target='verificationCard']")
    if (card) {
      card.remove()
      this.appendZennaMessage("No problem, let me know if you want to try again.")
    }
  }

  // --- Form filling ---

  applyToForm(fields) {
    const form = document.querySelector("form.ai-fillable")
    if (!form) {
      sessionStorage.setItem("zenna_prefill", JSON.stringify(fields))
      const path = window.location.pathname
      const dest = path.startsWith("/dashboard/hotel_bookings")
        ? "/dashboard/hotel_bookings/new"
        : "/dashboard/bookings/new"
      this.appendZennaMessage("Taking you to the booking form — I'll fill it in automatically.")
      setTimeout(() => { window.location.href = dest }, 800)
      return
    }

    this.formSnapshot = this.snapshotForm(form)
    this.lastAppliedFields = { ...fields }

    Object.entries(fields).forEach(([key, value]) => {
      const input = form.querySelector(`[name*="[${key}]"]`)
      if (!input) return

      if (input.tagName === "SELECT") {
        const option = Array.from(input.options).find(o => o.value === String(value))
        if (option) input.value = option.value
      } else if (input.type === "checkbox") {
        input.checked = Boolean(value)
      } else if (input.type === "datetime-local") {
        const raw = String(value).replace(" ", "T").replace(/Z$/, "").replace(/([+-]\d{2}:?\d{2})$/, "")
        let normalized = raw.length > 16 ? raw.replace(/:\d{2}(\.\d+)?$/, "").substring(0, 16) : raw.substring(0, 16)
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
          const parsed = new Date(value)
          if (!isNaN(parsed.getTime())) {
            const pad = n => String(n).padStart(2, "0")
            normalized = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
          }
        }
        input.value = normalized
      } else {
        input.value = value
      }

      input.classList.add("zenna-field-flash")
      setTimeout(() => input.classList.remove("zenna-field-flash"), 800)

      input.dispatchEvent(new Event("change", { bubbles: true }))
      input.dispatchEvent(new Event("input", { bubbles: true }))
    })

    // If starts_at was filled and the form has a separate date input (dashboard booking form),
    // explicitly populate it — Stimulus's startsAtChanged handler is not reliable enough
    // because hidden inputs don't always propagate synthetic change events correctly.
    if (fields.starts_at) {
      const date = String(fields.starts_at).substring(0, 10) // "YYYY-MM-DD"
      const dateInput = form.querySelector('[data-dashboard-booking-target="dateInput"]') ||
                        form.querySelector('input[type="date"]')
      if (dateInput && date) {
        dateInput.value = date
        dateInput.dispatchEvent(new Event("change", { bubbles: true }))
        dateInput.dispatchEvent(new Event("input", { bubbles: true }))
      }
    }

    this.showUndoBar()
  }

  snapshotForm(form) {
    const snapshot = {}
    form.querySelectorAll("input, select, textarea").forEach(field => {
      if (!field.name) return
      snapshot[field.name] = field.type === "checkbox" ? field.checked : field.value
    })
    return snapshot
  }

  // --- Undo ---

  showUndoBar() {
    this.undoBarTarget.classList.remove("hidden")

    if (this.undoTimeout) clearTimeout(this.undoTimeout)
    this.undoTimeout = setTimeout(() => this.hideUndoBar(), 30000)
  }

  hideUndoBar() {
    this.undoBarTarget.classList.add("hidden")
    if (this.undoTimeout) {
      clearTimeout(this.undoTimeout)
      this.undoTimeout = null
    }
  }

  undoFormFill() {
    if (!this.formSnapshot) return

    const form = document.querySelector("form.ai-fillable") || document.querySelector("main form")
    if (!form) return

    Object.entries(this.formSnapshot).forEach(([name, value]) => {
      const field = form.querySelector(`[name="${name}"]`)
      if (!field) return

      if (field.type === "checkbox") {
        field.checked = value
      } else {
        field.value = value
      }
      field.dispatchEvent(new Event("change", { bubbles: true }))
      field.dispatchEvent(new Event("input", { bubbles: true }))
    })

    this.formSnapshot = null
    this.lastAppliedFields = null
    this.hideUndoBar()
    this.appendZennaMessage("Reverted — the form is back to how it was.")
  }

  // --- Slot availability check ---

  async checkSlotAvailability(fields) {
    try {
      const startsAt = fields.starts_at
      // Parse just the date part (YYYY-MM-DD)
      const date = startsAt.substring(0, 10)
      const inRoom = fields.in_room ? "1" : "0"
      const url = `/dashboard/bookings/slots?treatment_id=${fields.treatment_id}&date=${date}&in_room=${inRoom}`

      const response = await fetch(url, { headers: { "Accept": "text/html" } })
      if (!response.ok) return { ok: true } // if endpoint fails, let form submit handle it

      const html = await response.text()

      // Check if the extracted ISO time appears as a slot button
      if (html.includes(`data-starts-at="${startsAt}"`)) {
        return { ok: true }
      }

      // Slot not available — extract alternatives from the HTML
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const buttons = doc.querySelectorAll("[data-starts-at]")
      const alternatives = Array.from(buttons).slice(0, 8).map(btn => ({
        starts_at: btn.dataset.startsAt,
        label: btn.textContent.trim()
      }))

      const msg = alternatives.length > 0
        ? "That time isn't available. Here are the closest available slots — pick one:"
        : "That time isn't available, and there are no other open slots on that date. Try a different day?"

      return { ok: false, message: msg, alternatives }
    } catch (e) {
      return { ok: true } // network error — let form submit catch it
    }
  }

  showAlternativeSlots(originalFields, alternatives) {
    const card = document.createElement("div")
    card.className = "mx-2 my-2 bg-cream border border-sand/40 rounded-lg p-3"

    const buttonsHtml = alternatives.map(slot => {
      const safeFields = this.escapeHtml(JSON.stringify({ ...originalFields, starts_at: slot.starts_at }))
      return `<button type="button"
        class="border border-sand rounded px-3 py-1.5 text-sm text-charcoal hover:bg-primary/10 hover:border-primary transition-colors"
        data-action="click->ai-chat#selectAlternativeSlot"
        data-starts-at="${slot.starts_at}"
        data-original-fields='${JSON.stringify({ ...originalFields, starts_at: slot.starts_at })}'
      >${this.escapeHtml(slot.label)}</button>`
    }).join("")

    card.innerHTML = `<div class="flex flex-wrap gap-2">${buttonsHtml}</div>`
    card.dataset.aiChatTarget = "alternativeSlots"
    this.messagesTarget.appendChild(card)
    this.scrollToBottom()
  }

  selectAlternativeSlot(event) {
    const fields = JSON.parse(event.currentTarget.dataset.originalFields)
    // Remove this alternatives card
    const card = event.currentTarget.closest("[data-ai-chat-target='alternativeSlots']")
    if (card) card.remove()
    this.showVerificationCard(fields, "high")
  }

  // --- Helpers ---

  hasRequiredFields(fields) {
    const context = this.contextValue || "general"
    if (context === "booking") {
      return fields.guest_name && fields.treatment_id && fields.starts_at
    } else if (context === "treatment") {
      return fields.name && fields.duration_minutes && fields.price_cents
    } else if (context === "therapist") {
      return fields.name
    }
    return Object.keys(fields).length > 0
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  humanizeFieldName(key) {
    const labels = {
      guest_name: "Guest Name",
      guest_email: "Email",
      guest_phone: "Phone",
      guest_room_number: "Room Number",
      guest_type: "Guest Type",
      treatment_id: "Treatment",
      starts_at: "Date / Time",
      source: "Source",
      in_room: "In-Room Service",
      guest_notes: "Guest Notes",
      staff_notes: "Staff Notes",
      name: "Name",
      description: "Description",
      duration_minutes: "Duration (min)",
      price_cents: "Price (Rp)",
      category: "Category",
      room_type_id: "Room Type",
      active: "Active",
      popular: "Popular",
      in_room_available: "In-Room Available",
      location_ids: "Locations",
      treatment_ids: "Treatments"
    }
    return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  getFieldInputType(key) {
    const selects = ["guest_type", "source", "treatment_id", "category", "room_type_id"]
    const checkboxes = ["in_room", "active", "popular", "in_room_available"]
    if (selects.includes(key)) return "select"
    if (checkboxes.includes(key)) return "checkbox"
    return "text"
  }

  buildSelectField(key, label, currentValue) {
    const options = this.getOptionsForField(key)

    if (options.length === 0) {
      return `
        <div>
          <label class="text-olive text-xs">${this.escapeHtml(label)}</label>
          <input type="text" data-field-key="${key}" value="${this.escapeHtml(String(currentValue))}" class="w-full border border-sand/40 rounded px-2 py-1 text-sm text-charcoal focus:border-zenna focus:ring-1 focus:ring-zenna/30" />
        </div>
      `
    }

    let optionsHtml = options.map(opt =>
      `<option value="${this.escapeHtml(String(opt.value))}" ${String(opt.value) === String(currentValue) ? "selected" : ""}>${this.escapeHtml(opt.label)}</option>`
    ).join("")

    return `
      <div>
        <label class="text-olive text-xs">${this.escapeHtml(label)}</label>
        <select data-field-key="${key}" class="w-full border border-sand/40 rounded px-2 py-1 text-sm text-charcoal focus:border-zenna focus:ring-1 focus:ring-zenna/30">
          ${optionsHtml}
        </select>
      </div>
    `
  }

  getOptionsForField(key) {
    switch (key) {
      case "guest_type":
        return [
          { value: "hotel_guest", label: "Hotel Guest" },
          { value: "non_hotel_guest", label: "Non-Hotel Guest" }
        ]
      case "source":
        return [
          { value: "hotel", label: "Hotel" },
          { value: "instagram", label: "Instagram" },
          { value: "whatsapp", label: "WhatsApp" },
          { value: "walk_in", label: "Walk-in" },
          { value: "others", label: "Others" }
        ]
      default:
        return []
    }
  }
}
